'use strict';

import AppDispatcher from '../AppDispatcher';
import ActionTypes from '../constants/ActionTypes';
import AlertTypes from '../constants/AlertTypes';
import update from 'react/lib/update';
import guid from 'simple-guid';
import StampStore from '../stores/StampStore';
import isSectionEmpty from '../utils/isSectionEmpty';
import dangerouslyStripHTML from '../utils/dangerouslyStripHTML';
import SectionTypes from '../constants/SectionTypes';
import UserStore from './UserStore';
import Legacy from '../utils/Legacy';
import cloneDeep from 'lodash/lang/cloneDeep';
import { find, isEmpty, reject, chain } from 'underscore';
import { createListStore, createListActionHandler } from '../utils/PaginatedStoreUtils';
import StampStatus from '../constants/StampStatus';

var _stamp = null,
    _isPublishing = false,
    _hasUnsavedChanges = false,
    _loadingMedia = {},
    _loadingMediaBackground = {},
    _lastAddedItemUuid = null;

function getInitialSectionContent(sectionType) {
  var section = {
    type: sectionType,
    uuid: guid()
  };

  switch (sectionType) {
  case SectionTypes.COVER:
    return Object.assign({}, section, {
      background: null,
      title: '',
      subtitle: ''
    });
  case SectionTypes.GALLERY:
    return Object.assign({}, section, {
      layout: 'list',
      images: []
    });
  case SectionTypes.POSTER:
    return Object.assign({}, section, {
      background: null
    });
  case SectionTypes.TEXT:
    return Object.assign({}, section, {
      text: ''
    });
  case SectionTypes.MEDIA:
    return section;
  }
}

function getInitialDraft() {
  const legacyDraft = Legacy.loadDraftFromStorage();
  const initialDraft = {
    user: UserStore.getMe().id,
    canEdit: true,
    content: {
      sections: [getInitialSectionContent(SectionTypes.COVER)]
    }
  };

  if (legacyDraft) {
    return Object.assign(initialDraft, legacyDraft);
  } else {
    return initialDraft;
  }
}

function getSections() {
  return _stamp.content.sections;
}

function findSection(sectionId) {
  var sections = getSections();
  return sections.indexOf(find(sections, section => section.uuid === sectionId));
}

function findImage(gallery, imageId) {
  var images = gallery.images;
  return images.indexOf(find(images, image => image.uuid === imageId));
}

function prepareSection(section, index) {
  if (index === 0) {
    section.subtitle = dangerouslyStripHTML(section.subtitle);
  }

  if (!section.uuid) {
    section.uuid = guid();
  }

  if (section.background) {
    section.background.caption = dangerouslyStripHTML(section.background.caption);
  }

  if (section.images) {
    section.images.forEach(image => {
      image.uuid = image.uuid || guid();
      image.caption = dangerouslyStripHTML(image.caption);
    });
  }
}

function updateStampFromCoverSection() {
  _stamp = update(_stamp, {
    $merge: {
      title: _stamp.content.sections[0].title,
      description: _stamp.content.sections[0].subtitle,
      thumbnail: _stamp.content.sections[0].background
    }
  });
}

function loadDraft(stamp) {
  _hasUnsavedChanges = false;

  if (!stamp) {
    _stamp = null;
  } else {
    _stamp = cloneDeep(stamp);
    _stamp.content.sections.forEach(prepareSection);
    updateStampFromCoverSection();
  }
}

function markNeedsSave() {
  _hasUnsavedChanges = true;
}

function addSection(sectionType, afterSectionId, initialOptions) {
  var index = findSection(afterSectionId),
      newSection;

  newSection = getInitialSectionContent(sectionType);
  _lastAddedItemUuid = newSection.uuid;

  if (initialOptions) {
    newSection = Object.assign({}, newSection, cloneDeep(initialOptions));
  }

  _stamp = update(_stamp, {
    content: {
      sections: {
        $splice: [[index + 1, 0, newSection]]
      }
    }
  });
}

function updateSection(index, sectionUpdate) {
  var sectionsUpdate = {};
  sectionsUpdate[index] = sectionUpdate;

  _stamp = update(_stamp, {
    content: {
      sections: sectionsUpdate
    }
  });

  if (index === 0) {
    updateStampFromCoverSection();
  }
}

function updateMediaSection(sectionId, data) {
  var index = findSection(sectionId),
      updateData = { $merge: data };

  if (index > -1) {
    updateSection(index, updateData);
  }
}

function moveSection(sectionId, afterSectionId) {
  var index = findSection(sectionId),
      section = _stamp.content.sections[index],
      afterIndex = findSection(afterSectionId);

  _stamp = update(_stamp, {
    content: {
      sections: {
        $splice: [
          [index, 1],
          [afterIndex, 0, section]
        ]
      }
    }
  });
}

function deleteSection(sectionId) {
  var index = findSection(sectionId);

  _stamp = update(_stamp, {
    content: {
      sections: {
        $splice: [[index, 1]]
      }
    }
  });
}

function addGalleryImage(afterSectionId, image) {
  var index = findSection(afterSectionId),
      section = _stamp.content.sections[index],
      atIndex = section.images.length;

  _lastAddedItemUuid = image.uuid;

  updateSection(index, {
    images: {
      $splice: [[atIndex, 0, image]]
    }
  });
}

function updateGalleryImage(sectionId, imageId, data) {
  var index = findSection(sectionId),
      gallery = _stamp.content.sections[index],
      imageIndex = findImage(gallery, imageId),
      updateData;

  updateData = { images: {} };
  updateData.images[imageIndex] = {
    $merge: data
  };

  updateSection(index, updateData);
}

function moveGalleryImage(sectionId, imageId, afterImageId) {
  var index = findSection(sectionId),
      gallery = _stamp.content.sections[index],
      imageIndex = findImage(gallery, imageId),
      image = gallery.images[imageIndex],
      afterImageIndex = findImage(gallery, afterImageId);

  updateSection(index, {
    images: {
      $splice: [
        [imageIndex, 1],
        [afterImageIndex, 0, image]
      ]
    }
  });
}

function deleteGalleryImage(sectionId, imageId) {
  var index = findSection(sectionId),
      gallery = _stamp.content.sections[index],
      imageIndex = findImage(gallery, imageId);

  updateSection(index, {
    images: {
      $splice: [[imageIndex, 1]]
    }
  });
}

function setStatus(status) {
  _stamp = update(_stamp, {
    status: {
      $set: status
    }
  });
}

var StampDraftStore = createListStore({
  getSections: getSections,

  getDraftStamp() {
    return _stamp;
  },

  isUnlisted() {
    return _stamp && _stamp.status === StampStatus.UNLISTED || false;
  },

  getSectionIndex(sectionId) {
    return findSection(sectionId);
  },

  isEmpty() {
    if (!_stamp) {
      return false;
    }

    var sections = getSections(),
        coverSection = sections[0];

    return !sections.length ||
            sections.length === 1 && !coverSection.title && !coverSection.subtitle && !coverSection.background;
  },

  hasContentOtherThanCover() {
    return reject(getSections(), isSectionEmpty).length > 1;
  },

  hasUnsavedChanges() {
    return _hasUnsavedChanges;
  },

  getMissingContentMessage() {
    var coverSection = getSections()[0],
        isEmptyTitle = isEmpty(coverSection.title),
        isEmptyBackground = isEmpty(coverSection.background);

    if (isEmptyTitle && isEmptyBackground) {
      return AlertTypes.EDITOR_COVER_TITLE_REQUIRED;
    } else if (isEmptyTitle && !isEmptyBackground) {
      return AlertTypes.EDITOR_TITLE_REQUIRED;
    } else if (!isEmptyTitle && isEmptyBackground) {
      return AlertTypes.EDITOR_COVER_REQUIRED;
    } else if (!this.hasContentOtherThanCover()) {
      return AlertTypes.EDITOR_ADD_MORE_CONTENT;
    }
  },

  isReady() {
    return Boolean(_stamp);
  },

  isPublishing() {
    return _isPublishing;
  },

  isLoadingMedia(sectionId) {
    return _loadingMedia[sectionId];
  },

  isLoadingMediaBackground(sectionId) {
    return _loadingMediaBackground[sectionId];
  },

  canPublish() {
    return this.isReady() && !_isPublishing;
  },

  getDraftImages() {
    if (!_stamp) {
      return [];
    }

    return chain(_stamp.content.sections)
      .map(section => {
        switch (section.type) {
        case SectionTypes.COVER:
        case SectionTypes.POSTER:
        case SectionTypes.MEDIA:
          return section.background;
        case SectionTypes.GALLERY:
          return section.images;
        }
      })
      .flatten().compact().value();
  },

  isLastAddedItem(uuid) {
    return _lastAddedItemUuid === uuid;
  },

  getDrafts() {
    return this.getIds(UserStore.getMe().id).map(StampStore.get);
  }
});

var handleListAction = createListActionHandler({
  request: ActionTypes.REQUEST_DRAFTS,
  error: ActionTypes.REQUEST_DRAFTS_ERROR,
  success: ActionTypes.REQUEST_DRAFTS_SUCCESS
});

AppDispatcher.register(function (payload) {
  AppDispatcher.waitFor([StampStore.dispatchToken]);

  var action = payload.action,
      updateData,
      list;

  if (UserStore.isAuthenticated()) {
    list = StampDraftStore.getList();
    handleListAction(action, list, StampDraftStore.emitChange);
  }

  switch (action.type) {
  case ActionTypes.INVALIDATE_DRAFTS:
    StampDraftStore.invalidate();
    break;

  case ActionTypes.SAVE_DRAFT_SUCCESS:
    _hasUnsavedChanges = false;

    if (!_stamp.uuid) {
      _stamp.uuid = action.draftUuid;
    }

    if (!list.contains(action.draftUuid)) {
      list.prepend(action.draftUuid);
    }

    StampDraftStore.emitChange();
    break;

  case ActionTypes.DELETE_DRAFT_SUCCESS:
    list.remove(action.draftUuid);
    loadDraft(null);
    StampDraftStore.emitChange();
    break;

  case ActionTypes.LOAD_EMPTY_DRAFT:
    loadDraft(getInitialDraft());

    // Remove this when we disable migrating legacy drafts:
    if (!StampDraftStore.isEmpty()) {
      markNeedsSave();
    }

    StampDraftStore.emitChange();
    break;

  case ActionTypes.LOAD_DRAFT_IN_EDITOR:
    loadDraft(null);
    StampDraftStore.emitChange();
    break;

  case ActionTypes.LOAD_DRAFT_IN_EDITOR_SUCCESS:
    loadDraft(StampStore.get(action.draftUuid));
    StampDraftStore.emitChange();
    break;

  case ActionTypes.LOAD_STAMP_IN_EDITOR:
    loadDraft(null);
    StampDraftStore.emitChange();
    break;

  case ActionTypes.LOAD_STAMP_IN_EDITOR_SUCCESS:
    loadDraft(StampStore.get(action.stampId));
    StampDraftStore.emitChange();
    break;

  case ActionTypes.UPDATE_STAMP:
  case ActionTypes.POST_STAMP:
    _isPublishing = true;
    StampDraftStore.emitChange();
    break;

  case ActionTypes.UPDATE_STAMP_ERROR:
  case ActionTypes.POST_STAMP_ERROR:
    _isPublishing = false;
    StampDraftStore.emitChange();
    break;

  case ActionTypes.UPDATE_STAMP_SUCCESS:
  case ActionTypes.POST_STAMP_SUCCESS:
    _isPublishing = false;
    list.remove(_stamp.uuid);
    loadDraft(null);
    StampDraftStore.emitChange();
    break;

  case ActionTypes.CANVAS_ADD_SECTION:
    addSection(action.sectionType, action.afterSectionId, action.initialOptions);
    markNeedsSave();
    StampDraftStore.emitChange();
    break;

  case ActionTypes.CANVAS_UPDATE_SECTION:
    updateData = { $merge: cloneDeep(action.data) };
    updateSection(findSection(action.sectionId), updateData);
    markNeedsSave();
    StampDraftStore.emitChange();
    break;

  case ActionTypes.CANVAS_MOVE_SECTION:
    moveSection(action.sectionId, action.afterSectionId);
    markNeedsSave();
    StampDraftStore.emitChange();
    break;

  case ActionTypes.CANVAS_DELETE_SECTION:
    deleteSection(action.sectionId);
    markNeedsSave();
    StampDraftStore.emitChange();
    break;

  case ActionTypes.GALLERY_ADD_IMAGE:
    addGalleryImage(action.sectionId, action.image);
    markNeedsSave();
    StampDraftStore.emitChange();
    break;

  case ActionTypes.GALLERY_UPDATE_IMAGE:
    updateGalleryImage(action.sectionId, action.imageId, action.data);
    markNeedsSave();
    StampDraftStore.emitChange();
    break;

  case ActionTypes.GALLERY_MOVE_IMAGE:
    moveGalleryImage(action.sectionId, action.imageId, action.afterImageId);
    markNeedsSave();
    StampDraftStore.emitChange();
    break;

  case ActionTypes.GALLERY_DELETE_IMAGE:
    deleteGalleryImage(action.sectionId, action.imageId);
    markNeedsSave();
    StampDraftStore.emitChange();
    break;

  case ActionTypes.REQUEST_MEDIA_DATA:
    _loadingMedia[action.sectionId] = true;
    StampDraftStore.emitChange();
    break;

  case ActionTypes.REQUEST_MEDIA_DATA_SUCCESS:
    delete _loadingMedia[action.sectionId];
    updateMediaSection(action.sectionId, action.data);
    markNeedsSave();
    StampDraftStore.emitChange();
    break;

  case ActionTypes.REQUEST_MEDIA_DATA_ERROR:
    delete _loadingMedia[action.sectionId];
    StampDraftStore.emitChange();
    break;

  case ActionTypes.UPLOAD_MEDIA_THUMBNAIL:
    _loadingMediaBackground[action.sectionId] = true;
    StampDraftStore.emitChange();
    break;

  case ActionTypes.UPLOAD_MEDIA_THUMBNAIL_ERROR:
    delete _loadingMediaBackground[action.sectionId];
    updateMediaSection(action.sectionId, {
      background: null
    });

    markNeedsSave();
    StampDraftStore.emitChange();
    break;

  case ActionTypes.UPLOAD_MEDIA_THUMBNAIL_SUCCESS:
    updateMediaSection(action.sectionId, {
      background: cloneDeep(action.image)
    });
    delete _loadingMediaBackground[action.sectionId];

    markNeedsSave();
    StampDraftStore.emitChange();
    break;

  case ActionTypes.DRAFT_RESET_LAST_ADDED_ITEM:
    _lastAddedItemUuid = null;
    StampDraftStore.emitChange();
    break;

  case ActionTypes.DRAFT_SET_STATUS:
    setStatus(action.status);
    StampDraftStore.emitChange();
    break;
  }
});

module.exports = StampDraftStore;



/** WEBPACK FOOTER **
 ** ./stores/StampDraftStore.js
 **/